import { debugNoCode } from '@unifyapps/carbon/consts/debug';
import type { EntityId } from '@unifyapps/carbon/dependency/manager/types';
import type { BuildDependencyGraphItem } from '@unifyapps/carbon/dependency/utils/buildDependencyGraph';
import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { JavaScriptFunction } from '@unifyapps/defs/types/javascriptFunction';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import { DependencyGraphManager } from '../../../dependency/manager/DependencyGraphManager';
import { DependencyGraphSubscriptions } from '../../../dependency/subscription/DependencyGraphSubscriptions';
import InterfacePageHelper from '../../helper/InterfacePage';
import type { UnsavedDataSource } from '../../stores/DataSourceRecordStore';
import type BlocksRegistry from '../RegistryProvider/BlocksRegistry';
import {
  blockDependencyBuilder,
  dataSourceDependencyBuilder,
  pageFieldDependencyBuilder,
  pageFunctionDependencyBuilder,
} from './builders';
import {
  computeRepeatableAncestorMap,
  type RepeatableAncestorMap,
} from './computeRepeatableAncestorMap';
import { getHandleSavedDependencies } from './hooks/getHandleSavedDependencies';
import type { EntityListType, EntityTypes } from './types';

export type BuildInstanceContextOptions = {
  /** forward per-node change to an external bus (app-level graphs use this). */
  notifyExternalDependencies?: (id: EntityId) => void;
  /** explicit page id; defaults to interfacePage?.id. */
  interfacePageId?: string;
  /** explicit flag; defaults to interfacePage.flags.shouldUseBuiltDependencies. */
  shouldUseBuiltDependencies?: boolean;
};

/**
 * Build one self-contained dependency graph context for a single page (outer
 * or per-instance). Owns its own graphManager + subscriptionManager pair (and
 * builder twins), plus the actions and rebuild hook consumers talk to.
 *
 * The returned context is intended to be stable for the lifetime of its owner
 * (provider or manager slot). Content refreshes go through shouldRebuildGraph,
 * which reset()s graphManager and replays the entity list — subscribers on
 * subscriptionManager survive.
 */
export function buildInstanceContext(
  getEntityList: () => EntityListType,
  interfacePage: InterfacePageEntity | undefined,
  registry: BlocksRegistry,
  options: BuildInstanceContextOptions = {},
) {
  // main graph (runtime) + its twin (builder) — same entity ids, different use.
  const graphManager = new DependencyGraphManager();
  const subscriptionManager = new DependencyGraphSubscriptions(graphManager);
  const builderGraphManager = new DependencyGraphManager();
  const builderSubscriptionManager = new DependencyGraphSubscriptions(builderGraphManager);

  const interfacePageId = options.interfacePageId ?? interfacePage?.id ?? '';
  const shouldUseBuiltDependencies =
    options.shouldUseBuiltDependencies ??
    Boolean(InterfacePageHelper.getFlags(interfacePage)?.shouldUseBuiltDependencies);

  // Block id → inner-first list of runtime-source ancestors (Repeatables).
  // Recomputed once per `buildGraph()` (i.e. when the block tree mutates),
  // never on the keystroke hot path. See `computeRepeatableAncestorMap.ts`
  // for cost analysis. `Map.get` returns `undefined` for blocks that don't
  // live under a Repeatable — most notify calls hit that fast path.
  let blockIdToRepeatableAncestors: RepeatableAncestorMap = new Map();

  const notifyDependencyChange = ({ id }: { id: EntityId }) => {
    subscriptionManager.notifyChange({ id });
    options.notifyExternalDependencies?.(id);

    // v1 Repeatable subscribers root their dpOn at the parent Repeatable's
    // id, not at the runtime child's id. When a runtime instance state
    // changes, fan out to every Repeatable ancestor so those subscribers
    // re-evaluate. Bounded by tree depth (typically 1–3). No allocations
    // — `ancestors` is the shared array from the prebuilt map.
    const ancestors = blockIdToRepeatableAncestors.get(id);
    if (!ancestors) {
      debugNoCode(`No ancestors found for ${id}`);
      return;
    }
    for (const ancestorId of ancestors) {
      subscriptionManager.notifyChange({ id: ancestorId });
      options.notifyExternalDependencies?.(ancestorId);
    }
  };

  const deleteDependencies = (id: EntityId) => {
    graphManager.deleteNode(id);
    subscriptionManager.removeSubscriptions(id);
    builderGraphManager.deleteNode(id);
  };

  const updateDependenciesAndNotify = (
    items: Record<EntityId, BuildDependencyGraphItem<unknown> | undefined>,
  ) => {
    graphManager.updateNodes(items);
    for (const nodeId of Object.keys(items)) {
      notifyDependencyChange({ id: nodeId } as { id: EntityId });
    }
  };

  const updateDependencies = (
    items: Record<EntityId, BuildDependencyGraphItem<unknown> | undefined>,
  ) => {
    graphManager.updateNodes(items);
  };

  const actions = {
    deleteDependencies,
    updateDependenciesAndNotify,
    notifyDependencyChange,
    updateDependencies,
  };

  const { handleSavedDependencies, buildNewDependencies } = getHandleSavedDependencies(
    registry,
    interfacePageId,
  );

  const buildGraph = (entityList: EntityListType) => {
    graphManager.reset();
    if (shouldUseBuiltDependencies) {
      handleSavedDependencies({ graphManager, entityList });
    } else {
      buildNewDependencies({ entityList, graphManager });
    }
    // Refresh the ancestor map at the same cadence as the dep graph itself —
    // both invalidate when the block tree changes.
    blockIdToRepeatableAncestors = computeRepeatableAncestorMap(entityList.blocks);
    subscriptionManager.notifyAll();
  };

  /**
   * Incremental dependency builder — used by the builder flow when a single
   * entity is edited. Reads a fresh entity list to resolve `blocks[blockId]`.
   */
  const buildEntityDependency = (entity: unknown, entityType: EntityTypes) => {
    const entityList = getEntityList();
    const blocks = entityList.blocks;
    switch (entityType) {
      case 'blocks': {
        const _entity = entity as BlockStateUnionType;
        const blockId = _entity.originalBlockId ?? _entity.id;
        if (!blockId) return {};
        const block = blocks[blockId];
        if (!block) return {};
        return blockDependencyBuilder({ blockState: _entity, block });
      }
      case 'pageFunctions':
        return pageFunctionDependencyBuilder({ pageFunction: entity as JavaScriptFunction });
      case 'dataSources':
        return dataSourceDependencyBuilder({ dataSource: entity as UnsavedDataSource });
      case 'documentTitle':
        return pageFieldDependencyBuilder({
          fieldKey: 'documentTitle',
          fieldValue: entity as string,
        });
      case 'pageFieldDependencies':
        throw new Error('page field dependencies not meant to built together at runtime');
      case 'pageVariables':
        throw new Error('page variables not handled');
    }
  };

  const shouldRebuildGraph = () => {
    buildGraph(getEntityList());
  };

  // initial build — call sites (providers / instance manager) rely on the
  // graph being populated as soon as the context is handed out.
  buildGraph(getEntityList());

  return {
    graphManager,
    builderGraphManager,
    subscriptionManager,
    builderSubscriptionManager,
    buildEntityDependency,
    shouldRebuildGraph,
    actions,
  };
}

export type BuildInstanceContextReturnType = ReturnType<typeof buildInstanceContext>;
